import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { API_BASE_URL as API_BASE } from '../../../axiosSetup.js';
import { getPriceList, getProductTypes } from '../../../api/priceListApi.js';
import { useRetryFetch } from '../hooks/useRetryFetch.js';
import Dropdown from '../components/Dropdown.jsx';
import Pagination from '../components/Pagination.jsx';
import Spinner from '../components/Spinner.jsx';
import WarningIcon from '../components/WarningIcon.jsx';
import Tooltip from '../components/Tooltip.jsx';
import { classifyPortalError } from '../utils/apiError.js';
import { showErrorToast } from '../utils/toast.js';
import './PriceList.css';

const PAGE_SIZE_OPTIONS = [10, 25, 50];

function formatPrice(amount, currency) {
  if (amount === undefined || amount === null || Number.isNaN(Number(amount))) return '—';
  return `${currency ? currency + ' ' : ''}${Number(amount).toFixed(2)}`;
}

function ProductImageCell({ item }) {
  const [hasError, setHasError] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [previewRect, setPreviewRect] = useState(null);
  const wrapperRef = useRef(null);

  const imageSrc = item.imageUrl
    ? item.imageUrl.startsWith('https')
      ? item.imageUrl
      : `${API_BASE}/customer/product/${item.id}/image`
    : null;
  const showImage = Boolean(imageSrc) && !hasError;

  if (!showImage) {
    return <div className="customer-portal-price-list-table__image" />;
  }

  const handleMouseEnter = () => {
    if (wrapperRef.current) {
      setPreviewRect(wrapperRef.current.getBoundingClientRect());
    }
    setHovered(true);
  };

  return (
    <div
      className="customer-portal-price-list-table__image customer-portal-price-list-table__image--photo"
      ref={wrapperRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setHovered(false)}
    >
      <img src={imageSrc} alt={item.productCode} width={34} height={34} onError={() => setHasError(true)} />
      {hovered && previewRect && createPortal(
        <div
          className="customer-portal-price-list-table__image-preview"
          style={{ top: previewRect.top, left: previewRect.right }}
        >
          <img src={imageSrc} alt={item.productCode} />
        </div>,
        document.body
      )}
    </div>
  );
}

const JUSTIFY_BY_ALIGN = { left: 'flex-start', center: 'center', right: 'flex-end' };

function SortableCol({ label, sortKey, sort, onSort, align = 'left' }) {
  const active = sort.key === sortKey;
  return (
    <button
      type="button"
      className="customer-portal-orders-full-table__sortable"
      style={{ justifyContent: JUSTIFY_BY_ALIGN[align] }}
      onClick={() => onSort(sortKey)}
    >
      {label}
      <span className="customer-portal-orders-full-table__sort-icons" aria-hidden="true">
        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75"
          className={active && sort.dir === 'asc' ? 'customer-portal-is-active' : ''}>
          <polyline points="18 15 12 9 6 15" />
        </svg>
        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75"
          className={active && sort.dir === 'desc' ? 'customer-portal-is-active' : ''}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </span>
    </button>
  );
}

const SORT_ORDER_MAP = { asc: 'ASC', desc: 'DESC' };

export default function PriceList() {
  const [productTypes, setProductTypes] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [productType, setProductType] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sort, setSort] = useState({ key: '', dir: '' });

  useEffect(() => {
    getProductTypes()
      .then((types) => setProductTypes(types))
      .catch((err) => {
        setProductTypes([]);
        showErrorToast(classifyPortalError(err).message || "Couldn't load product types. Please try again.");
      });
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, loading, error, errorMessage, manualRetries, retry } = useRetryFetch(
    () => getPriceList({
      search,
      productType,
      page,
      pageSize,
      sortField: sort.key,
      sortOrder: SORT_ORDER_MAP[sort.dir] || '',
    }),
    [search, productType, page, pageSize, sort]
  );

  const items = data?.items ?? [];
  const totalPages = data?.totalPages ?? 1;
  const currency = data?.currency ?? '';
  // Prefer the backend's real total item count; fall back to a best-effort
  // estimate (page count * page size) for as long as the API doesn't send one.
  const total = data?.total ?? (totalPages * pageSize);
  const from = items.length === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = (page - 1) * pageSize + items.length;

  const toggleSort = (key) => {
    setPage(1);
    setSort((s) => {
      if (s.key !== key) return { key, dir: 'asc' };
      if (s.dir === 'asc') return { key, dir: 'desc' };
      return { key: '', dir: '' };
    });
  };

  return (
    <div className="customer-portal-page-content">
      <div className="customer-portal-orders-header">
        <div>
          <div className="customer-portal-orders-header__title">Price List</div>
        </div>
      </div>

      <div className="customer-portal-card customer-portal-filter-bar">
        <div className="customer-portal-filter-bar__search">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Search product code or description"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <div className="customer-portal-price-list-type-select">
          <Dropdown
            value={productType}
            onChange={(v) => { setProductType(v); setPage(1); }}
            placeholder="Select Product Type"
            options={[{ value: '', label: 'All' }, ...productTypes.map((t) => ({ value: t, label: t }))]}
            searchable
            searchPlaceholder="Search product types…"
            showValueTooltip
            showOptionTooltip
          />
        </div>
      </div>

      <div className="customer-portal-card customer-portal-price-list-table">
        <div className="customer-portal-price-list-table__cols">
          <span className="customer-portal-orders-full-table__left">Product Image</span>
          <SortableCol label="Product Code" sortKey="productCode" sort={sort} onSort={toggleSort} align="left" />
          <SortableCol label="Product Description" sortKey="productName" sort={sort} onSort={toggleSort} align="left" />
          <span className="customer-portal-orders-full-table__center">Product Type</span>
          <SortableCol label="Unit Price" sortKey="customerPrice" sort={sort} onSort={toggleSort} align="right" />
        </div>

        {loading && <div className="customer-portal-card__loader"><Spinner size={32} /></div>}

        {error && (
          <div className="customer-portal-card__error">
            <WarningIcon />
            <span>{errorMessage || "Couldn't load the price list"}</span>
            {manualRetries > 0 && (
              <button
                onClick={retry}
                className="customer-portal-btn customer-portal-btn--primary customer-portal-btn--sm"
              >
                Retry
              </button>
            )}
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="customer-portal-orders-full-table__empty">No products are mapped to your account.</div>
        )}

        {!loading && !error && items.map((item) => (
          <div className="customer-portal-price-list-table__row" key={item.id ?? item.productCode}>
            <ProductImageCell item={item} />
            <Tooltip as="span" className="customer-portal-price-list-table__code customer-portal-orders-full-table__left">{item.productCode}</Tooltip>
            <Tooltip as="span" className="customer-portal-price-list-table__desc">{item.productDescription}</Tooltip>
            <Tooltip as="span" className="customer-portal-price-list-table__type customer-portal-orders-full-table__center">{item.productType || '—'}</Tooltip>
            <Tooltip as="span" className="customer-portal-price-list-table__price">{formatPrice(item.total, currency)}</Tooltip>
          </div>
        ))}
      </div>

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        infoLabel={`${from}–${to} of ${total} products`}
        pageSize={pageSize}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
      />
    </div>
  );
}
